import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  readCookieConsent,
  writeCookieConsent,
  type CookieConsentValue,
} from "@/lib/cookieConsent";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])';

export const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const primaryActionRef = useRef<HTMLButtonElement | null>(null);
  const previousFocusedElementRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    setVisible(readCookieConsent() === null);
  }, []);

  useEffect(() => {
    if (!visible) return;

    const frame = window.requestAnimationFrame(() => {
      previousFocusedElementRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      primaryActionRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [visible]);

  const getFocusableElements = () => {
    const container = dialogRef.current;
    if (!container) return [] as HTMLElement[];

    return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (element) =>
        !element.hasAttribute("disabled") && element.getAttribute("aria-hidden") !== "true",
    );
  };

  const restoreFocus = () => {
    const previousFocusedElement = previousFocusedElementRef.current;
    if (!previousFocusedElement || !previousFocusedElement.isConnected) return;

    window.requestAnimationFrame(() => {
      previousFocusedElement.focus();
    });
  };

  const persist = (value: CookieConsentValue) => {
    try {
      writeCookieConsent(value);
    } catch {
      // localStorage indisponivel (modo privado estrito) - degradamos em silencio
    }
    setVisible(false);
    restoreFocus();
    // Notify any analytics provider listening for the change so it can
    // initialize (or refuse to initialize) without a page reload.
    window.dispatchEvent(new CustomEvent("orzio:cookie-consent-change", { detail: value }));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (event.key !== "Tab") return;

    const focusableElements = getFocusableElements();
    if (focusableElements.length === 0) {
      event.preventDefault();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    if (event.shiftKey) {
      if (!activeElement || activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
      return;
    }

    if (!activeElement || activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  if (!visible) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onKeyDown={handleKeyDown}
      className="fixed inset-x-0 bottom-0 z-[60] flex justify-center px-4 pb-4"
    >
      <div className="cockpit-card w-full max-w-3xl rounded-2xl border-0 bg-white/95 p-4 text-slate-900 shadow-xl backdrop-blur sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm leading-relaxed text-slate-700">
            <h2 id={titleId} className="block text-base font-semibold text-slate-900">
              Usamos cookies estritamente necessários.
            </h2>
            <p id={descriptionId}>
              Cookies opcionais (PostHog, Sentry) só são ativados se aceitar. Detalhes na{" "}
              <Link to="/legal/cookies" className="text-primary underline">
                Política de Cookies
              </Link>
              .
            </p>
          </div>
          <div className="flex flex-shrink-0 gap-2">
            <Button
              ref={primaryActionRef}
              variant="outline"
              size="sm"
              onClick={() => persist("essential_only")}
            >
              Apenas essenciais
            </Button>
            <Button size="sm" onClick={() => persist("accepted")}>
              Aceitar todos
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
